import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Button from "components/basic/button";
import { ProgressSteps } from "components/basic/progressSteps";
import Typography from "components/basic/typography";
import {
  useMeQuery,
  useSetOnboardingStepMutation,
  VendorVerification,
} from "generated/graphql";
import { loadProfileData } from "../profile/profileData";
import { useVendorFieldSave } from "../profile/helpers/useVendorFieldSave";
import DeleteAccountButton from "../profile/components/DeleteAccountButton";
import BrandIdentityFields from "../profile/formPages/brandIdentityProfileDashboard/components/BrandIdentityFields";
import AgreementStep from "./components/AgreementStep";
import AvailabilityStep from "./components/AvailabilityStep";
import DocumentsStep from "./components/DocumentsStep";
import ServicesStep from "./components/ServicesStep";
import {
  currentStep,
  missingRequiredDocuments,
  nextStep,
  OnboardingStep,
  STEP_LABELS,
  stepsFor,
} from "./helpers";

// Saved edits reach the page through the cache, so nothing is held locally.
const ignoreChange = () => {};

/** Guides a vendor from the agreement to their first service. */
const Onboarding: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation("onboarding");
  const navigate = useNavigate();
  const [{ data }] = useMeQuery();
  const [, saveStep] = useSetOnboardingStepMutation();
  const saveVendor = useVendorFieldSave();
  const me = data?.me;
  const profile = useMemo(() => (me ? loadProfileData(me) : null), [me]);
  const vendor = me?.vendor;
  const [step, setStep] = useState<OnboardingStep | null>(null);

  // Starts at the saved step; required steps take over whenever they apply.
  useEffect(() => {
    if (!vendor) {
      return;
    }
    setStep((shown) => {
      const resumed = currentStep(vendor);
      return shown && !["documents", "agreement"].includes(resumed)
        ? shown
        : resumed;
    });
  }, [vendor]);

  if (!me || !vendor || !profile?.vendor || !step) {
    return null;
  }

  const goTo = (next: OnboardingStep) => {
    setStep(next);
    saveStep({ step: next });
  };
  const advance = () => goTo(nextStep(vendor, step));
  const steps = stepsFor(vendor);
  const required = step === "documents" || step === "agreement";
  const approved = vendor.verificationStatus === VendorVerification.Approved;
  // Kept as the saved step, so the dashboard resumes here once approved.
  const awaitingApproval = step === "services" && !approved;
  // The dashboard sends vendors owing a document back here, so leaving for it
  // would bounce straight back; the rest of the site stays open to them.
  const dashboardLocked = missingRequiredDocuments(vendor);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        p: { xs: 2, sm: 4 },
      }}
    >
      <Typography variant="h4" style={{ fontWeight: "bold" }}>
        {t("title")}
      </Typography>

      {step !== "done" && (
        <ProgressSteps
          steps={steps.map((s) => ({ label: t(STEP_LABELS[s]) }))}
          currentStep={steps.indexOf(step)}
        />
      )}

      <Box sx={{ width: "100%", maxWidth: 560 }}>
        {step === "documents" && (
          <DocumentsStep vendor={profile.vendor} onSave={saveVendor} />
        )}
        {step === "agreement" && <AgreementStep onAccepted={advance} />}
        {step === "brand_identity" && (
          <BrandIdentityFields
            dashboardProfileData={profile}
            onChange={ignoreChange}
          />
        )}
        {step === "availability" && <AvailabilityStep onSaved={advance} />}
        {step === "services" && (
          <ServicesStep
            approved={approved}
            onAddService={() => {
              goTo("done");
              navigate("/dashboard/ServiceInstallation");
            }}
          />
        )}
        {step === "done" && (
          <Typography variant="body1" align="center">
            {t("done")}
          </Typography>
        )}
      </Box>

      {dashboardLocked && (
        <Typography
          variant="body2"
          align="center"
          style={{ color: theme.palette.text.secondary }}
        >
          {t("locked")}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
          maxWidth: 560,
        }}
      >
        {/* Deleting needs no onboarding, so it sits beside the way out. */}
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="text"
            onClick={() =>
              navigate(dashboardLocked ? "/" : "/dashboard/profile")
            }
          >
            {t(
              step === "done" || awaitingApproval
                ? "go_to_dashboard"
                : "finish_later",
            )}
          </Button>
          <DeleteAccountButton />
        </Box>
        {!required && step !== "done" && !awaitingApproval && (
          <Button
            variant="contained"
            color="warning"
            style={{ color: theme.palette.common.white }}
            onClick={advance}
          >
            {t(step === "brand_identity" ? "next" : "skip")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Onboarding;
